import { useCallback } from 'react'
import { useDispatch, useSelector } from 'store'
import { numberF, voidF } from 'types/FunctionTypes'
import * as SearchUserAction from 'slicers/SearchSlicers/SearchUserSlice'
import * as SearchLogAction from 'slicers/SearchSlicers/SearchLogSlice'
import * as TableJokenAction from 'slicers/TableSlicers/TableJuryoSlice'
import * as TableUserAction from 'slicers/TableSlicers/TableUserSlice'
import * as TableLogAction from 'slicers/TableSlicers/TableLogSlice'
import * as LoginAction from 'slicers/LoginSlice'
import * as ModalAction from 'slicers/ModalSlice'
import * as ModalFileAction from 'slicers/ModalFileSlice'
import * as ModalUserAction from 'slicers/ModalUserSlice'
import * as PageAction from 'slicers/PageSlice'
import * as SearchJuryoAction from 'slicers/SearchSlicers/SearchJuryoSlice'
import * as TableAction from 'slicers/TableSlicers/TableSlice'

// すべてのreducerを初期化 //================================//
export const useInitAllReducer = (): [voidF] => {
  const dispatch = useDispatch()
  const initAllReducer = useCallback(() => {
    dispatch(SearchJuryoAction.formatAll())
    dispatch(SearchUserAction.formatAll())
    dispatch(SearchLogAction.format())
    dispatch(TableAction.formatAll())
    dispatch(TableJokenAction.format())
    dispatch(TableUserAction.format())
    dispatch(TableLogAction.format())
    dispatch(LoginAction.format())
    dispatch(ModalFileAction.format())
    dispatch(ModalAction.format())
    dispatch(ModalUserAction.format())
    dispatch(PageAction.format())
  }, [])
  return [initAllReducer]
}

// 仮想テーブルの高さを画面の下までに設定 //================================//
export const useChangeTableHeight = (tableHeight: number, currentInnerHeight: number, previousInnerHeight: number, setTableHeight: React.Dispatch<React.SetStateAction<number>>) => {
  const changeTableHeight = useCallback(() => {
    if (currentInnerHeight > previousInnerHeight) {
      const diff = currentInnerHeight - previousInnerHeight
      setTableHeight(tableHeight + diff)
    } else if (document.documentElement.scrollHeight !== currentInnerHeight) {
      const diff = document.documentElement.scrollHeight - currentInnerHeight
      setTableHeight(tableHeight - diff)
    } else if (currentInnerHeight < previousInnerHeight) {
      const diff = previousInnerHeight - currentInnerHeight
      setTableHeight(tableHeight - diff)
    }
  }, [tableHeight, currentInnerHeight, previousInnerHeight])
  return [changeTableHeight]
}

// 現在と変更前のinnerHeightを設定 ※仮想テーブルの高さ設定に使用 //================================//
export const useChangeInnerHeight = (currentInnerHeight: number, setCurrentInnerHeight: React.Dispatch<React.SetStateAction<number>>, setPreviousInnerHeight: React.Dispatch<React.SetStateAction<number>>) => {
  const changeInnerHeight = useCallback(() => {
    // ウィンドウサイズ変更時に新しいinnreHeightと前のinnerHeightを更新
    if (currentInnerHeight !== window.innerHeight) {
      setPreviousInnerHeight(currentInnerHeight)
      setCurrentInnerHeight(window.innerHeight)
    }
  }, [window.innerHeight, currentInnerHeight])
  return [changeInnerHeight]
}

// 仮想テーブルの高さを画面の下までに設定。手入力画面で使用している。他画面の実装よりシンプルなのでこっちのほうがよさそう //================================//
export const useChangeTableHeightNew = (tableHeight: number, setTableHeight: React.Dispatch<React.SetStateAction<number>>): [voidF, numberF] => {
  // 新しいtableHeightを取得する
  const getNewTableHeight = () => {
    const el = document.getElementById('headerElement')
    const el2 = document.getElementById('bottomElement')
    const el3 = document.getElementById('toolbarElement')
    if (el && el2 && el3) {
      // テーブルの高さを画面上部から画面下固定の要素までの距離-画面上部からテーブル要素までの距離-ツールバーの高さを取得
      const newTableHeight = Math.ceil(el2.getBoundingClientRect().top - el.getBoundingClientRect().top - el3.clientHeight)
      return newTableHeight
    }
    return 0
  }

  const changeTableHeight = useCallback(() => {
    const newTableHeight = getNewTableHeight()
    if (newTableHeight > 0 && tableHeight !== newTableHeight) setTableHeight(newTableHeight)
  }, [tableHeight])
  return [changeTableHeight, getNewTableHeight]
}

// サイドメニュー、ヘッダータイトルを押したときの処理
export const useSetChoice = (history: any, location: any): [boolean, (selected: boolean, path: string) => void] => {
  const dispatch = useDispatch()
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const loading = useSelector((state) => state.PageReducer.loading)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const isSuper = useSelector((state) => state.LoginReducer.isSuper)

  const setChoice = useCallback(
    (selected: boolean, path: string) => {
      if (!selected && !loading) {
        dispatch(PageAction.changeLoading(true))
        switch (location.pathname) {
          case '/juryohyo':
            dispatch(SearchJuryoAction.initialize({ isJapell, isSuper: isSuper, JIGYO_CD: keyInfo[0].JIGYO_CD })) // 初期状態に設定
            dispatch(TableJokenAction.format())
            break
          case '/userToroku':
            dispatch(SearchUserAction.format())
            dispatch(TableUserAction.format())
            break
          case '/log':
            dispatch(SearchLogAction.format())
            dispatch(TableLogAction.format())
            break
        }
        dispatch(TableAction.format())
        dispatch(PageAction.format())
        if (path === '/changePassword') {
          // パスワード変更画面の氏名にユーザー名を設定
          // dispatch(LoginAction.changeInputUserName(NAME))
        }
        history.push(path)
      }
    },
    [loading],
  )
  return [isJapell, setChoice]
}

// サマリ画面初期表示時に処理 サマリ画面に来たらブラウザバックで明細に戻れなくするため
export const useFormatDetail = (path: string): [voidF] => {
  const dispatch = useDispatch()
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)

  const formatDetail = useCallback(() => {
    // サイドバー、ヘッダー以外でほかのメニューに飛んだあと戻ってきた場合、
    // 検索条件が残るため初期化する
    if (path !== '/log') {
      dispatch(SearchLogAction.format())
      dispatch(SearchLogAction.changeJigyoshoCode(String(keyInfo[0].JIGYO_CD)))
      dispatch(TableLogAction.format())
    }
    if (path !== '/log') dispatch(TableAction.format())
    dispatch(PageAction.format())
  }, [])
  return [formatDetail]
}
